'use client';

import { useEffect, useRef } from 'react';

import {
  ACCOUNT_PAGES,
  type AccountPage,
} from '@eliadmin/core/src/components/nt/account/shell/account-pages';
import {
  AccountUser,
  LoyaltyBox,
  SignOutLink,
} from '@eliadmin/core/src/components/nt/account/shell/account-user';
import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * The account navigation under 1024 px (the design has desktop frames only): the „Account /
 * Sidebar“ card turned into one block above the content – the user with „Odhlásiť sa“ (icon only on
 * phones), the loyalty box (under the user on phones, between them from 768 px) and the four pages
 * as a scroll-snap row of the sidebar items. The current page is scrolled into view.
 */
export function AccountCompactNav({ current }: { current?: AccountPage['key'] }) {
  const { t } = useTranslations();
  const activeRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const item = activeRef.current;
    const row = item?.parentElement;
    if (!item || !row) return;
    const itemBox = item.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    if (itemBox.left >= rowBox.left && itemBox.right <= rowBox.right) return;
    // Scroll the row only – `scrollIntoView` would move the page as well.
    row.scrollLeft += itemBox.left - rowBox.left - parseFloat(getComputedStyle(row).paddingLeft);
  }, [current]);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-[#E2E5DF] bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <AccountUser className="flex-1" />
        <LoyaltyBox className="order-last w-full md:order-none md:w-auto" />
        <SignOutLink className="shrink-0 p-2.5 sm:px-3 sm:py-1.5" labelClassName="max-sm:sr-only" />
      </div>
      <hr className="border-[#E2E5DF]" />
      <nav aria-label={t('text1734')}>
        <ul
          role="list"
          className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-1 overflow-x-auto px-4 [scrollbar-width:none] sm:-mx-5 sm:scroll-px-5 sm:px-5 [&::-webkit-scrollbar]:hidden"
        >
          {ACCOUNT_PAGES.map(page => {
            const active = page.key === current;
            return (
              <li key={page.key} ref={active ? activeRef : undefined} className="snap-start">
                <Link
                  href={page.href()}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 transition-colors focus-visible:shadow-focus focus-visible:outline-none',
                    active ? 'bg-[#F6FBF7] text-[#0E6A20]' : 'text-[#0D110D] hover:bg-[#F5F6F3]',
                  )}
                >
                  <Icon
                    name={page.icon}
                    size={20}
                    className={active ? 'text-[#127A26]' : 'text-[#5D655D]'}
                  />
                  <span className={active ? 'nt-body-l-bold' : 'nt-body-l'}>{t(page.label)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
